import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getLanguageByCode, DEFAULT_LANGUAGE, type Language } from "@/lib/languages";
import i18n from "@/i18n";

interface LanguageContextType {
  language: Language;
  setLanguage: (code: string) => Promise<void>;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

/**
 * Resolve the starting language synchronously on the client so the provider
 * never calls changeLanguage("en") first — i18next's detector caches whatever
 * it is given, which would otherwise clobber the stored preference.
 */
function initialLanguage(): Language {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;
  try {
    const stored = localStorage.getItem("preferred_language");
    if (stored) return getLanguageByCode(stored);
  } catch { /* ignore */ }
  return getLanguageByCode(navigator.language);
}

export function LanguageProvider({ children, userId }: { children: React.ReactNode; userId?: string | null }) {
  const [language, setLang] = useState<Language>(DEFAULT_LANGUAGE);
  const [ready, setReady] = useState(false);
  useEffect(() => { setLang(initialLanguage()); setReady(true); }, []);
  const [isLoading, setIsLoading] = useState(false);

  // Apply language to i18next + <html lang/dir> whenever it changes.
  useEffect(() => {
    if (!ready) return;
    void i18n.changeLanguage(language.code);
    if (typeof document !== "undefined") {
      document.documentElement.lang = language.code;
      document.documentElement.dir = language.dir === "rtl" ? "rtl" : "ltr";
    }
    try {
      localStorage.setItem("preferred_language", language.code);
    } catch { /* ignore */ }
  }, [language, ready]);



  useEffect(() => {
    if (!userId) return;
    supabase
      .from("profiles")
      .select("preferred_language")
      .eq("user_id", userId)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.preferred_language) {
          setLang(getLanguageByCode(data.preferred_language));
        }
      });
  }, [userId]);

  const setLanguage = useCallback(async (code: string) => {
    const lang = getLanguageByCode(code);
    setLang(lang);
    if (userId) {
      setIsLoading(true);
      await supabase
        .from("profiles")
        .update({ preferred_language: code } as any)
        .eq("user_id", userId);
      setIsLoading(false);
    }
  }, [userId]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
